import type { UserQuotaFastCleanListParams } from 'reactApp/api/quotaCleaner/QuotaCleanerAPICall.types';
import type { RootState } from 'reactApp/store';
import { type UserQuotaCleanerListParams, UserQuotaGroupId, UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { createSelector } from 'reselect';

import { getViewModes } from './helpers/getViewModes';
import { baseItemsLimit, imageItemsLimit } from './userQuotaCleaner.constants';
import type {
    QuotaCleaneFastGroup,
    QuotaCleanerGroup,
    QuotaCleanerState,
    QuotaCleanerYearConfig,
    QuotaCleanerYearGroupConfig,
    QuotaFastCleanerState,
} from './userQuotaCleaner.types';

export const getQuotaCleanerState = (state: RootState) => state.userQuotaCleaner as QuotaCleanerState;

export const getGroups = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.groups);
export const getNotEmptyGroups = createSelector(getGroups, (groups) => groups.filter((group) => group.childs.length));
export const getLimitedGroups = createSelector(getNotEmptyGroups, (groups) =>
    groups.map((group) => {
        const { groupId, childs, list } = group;
        const isImageContent = groupId === UserQuotaGroupId.CameraUploads || groupId === UserQuotaGroupId.Video;
        const limit = isImageContent ? imageItemsLimit : baseItemsLimit;

        const newChilds = childs.slice(0, limit);
        const newList = newChilds.reduce((acc, child) => {
            acc[child] = list[child];
            return acc;
        }, {});

        return {
            ...group,
            childs: newChilds,
            list: newList,
        };
    })
);

export const getFilteredLimitedGroups = createSelector(getLimitedGroups, (groups): QuotaCleanerGroup[] =>
    groups.sort((a, b) => b.size - a.size)
);

export const getGroupsParams = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.groupsParams);

// то же самое, что getGroupsParams, но чистим от лишних свойств, которые не нужны в запросе на easy_clear
const getFastGroupsParams = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => {
    const groupsParams = state.groupsParams;

    if (!groupsParams) {
        return null;
    }

    return Object.keys(groupsParams).reduce((acc, groupId) => {
        const params: Partial<UserQuotaCleanerListParams> = { ...groupsParams[groupId] };

        delete params.limit;
        delete params.offset;
        delete params.year_view;
        delete params.sort;
        delete params.order;

        acc[groupId] = params;

        return acc;
    }, {}) as Record<UserQuotaGroupId, UserQuotaFastCleanListParams>;
});

export const getGroupsIsLoading = createSelector(getQuotaCleanerState, (state) => state.isGroupsLoading);
export const getGroupsIsLoadingError = createSelector(getQuotaCleanerState, (state) => state.isGroupsLoadingError);
export const getGroupsIsLoaded = createSelector(getQuotaCleanerState, (state) => state.isGroupsLoaded);

export const getCurrentGroup = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.currentGroup);
export const getCurrentGroupId = createSelector(getCurrentGroup, (group) => group?.groupId);
const getCurrentGroupType = createSelector(getCurrentGroup, (currentGroup) => currentGroup?.groupType);
const isCurrentGroupLetter = createSelector(getCurrentGroupType, (groupType) => groupType === UserQuotaGroupType.Letter);
const isCurrentGroupCloud = createSelector(getCurrentGroupType, (groupType) => groupType === UserQuotaGroupType.Cloud);

export const getCurrentGroupTotalCount = createSelector(getCurrentGroup, (currentGroup) => currentGroup?.totalCount || 0);
export const getCurrentGroupTotalSize = createSelector(getCurrentGroup, (currentGroup) => currentGroup?.size || 0);

export const getGroupViewModes = createSelector(getCurrentGroup, (currentGroup) =>
    getViewModes(currentGroup?.groupType, currentGroup?.groupId)
);

export const getGroupById = createSelector(
    getGroups,
    (state, id) => id,
    (groups, id) => groups.find((group) => group.groupId === id)
);

export const getItemByGroup = createSelector(
    (state, itemId: string, groupId?: UserQuotaGroupId) => getGroupById(state, groupId),
    (state, itemId: string) => itemId,
    (group, itemId) => group?.list?.[itemId]
);

export const getItem = createSelector(
    (state, itemId: string) => itemId,
    getGroups,
    (itemId: string, groups: QuotaCleanerGroup[]) => {
        const group = groups.find((group: QuotaCleanerGroup) => group.childs.includes(itemId));
        return group?.list[itemId];
    }
);

export const getItemById = createSelector(
    (state) => state,
    (state, id: string) => id,
    getCurrentGroup,
    (state, id, currentGroup) => currentGroup?.list[id] || getItem(state, id)
);

export const getNeedGroupsReload = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.needGroupsReload);

export const getIsLetterGroup = createSelector(getCurrentGroup, (currentGroup) => currentGroup?.groupType === UserQuotaGroupType.Letter);

// все загруженные группы пустые, если загрузки еще нет - пустых групп тоже нет
export const isAllGroupsLoadedEmpty = createSelector(
    getGroupsIsLoaded,
    getNotEmptyGroups,
    (isLoaded, notEmptyGroups) => isLoaded && !notEmptyGroups.length
);

export const isAllGroupsEmpty = createSelector(getNotEmptyGroups, (notEmptyGroups) => !notEmptyGroups.length);

export const isMeesagesRemoveNeeded = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isMessagesRemoveNeeded);
export const isCloudRemoveNeeded = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isCloudRemoveNeeded);
export const trashFilesIds = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.trashFilesIds);
export const isSuccessMovedToTrash = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isSuccessMovedToTrash);
export const getYearGroupsConfig = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.yearGroupsConfig);
export const getYearGroupConfig = createSelector(
    getYearGroupsConfig,
    (state, groupId: UserQuotaGroupId | undefined) => groupId,
    (yearGroupsConfig: QuotaCleanerYearConfig, groupId) => {
        if (!groupId) {
            return;
        }

        const yearGroupConfig: QuotaCleanerYearGroupConfig | undefined = yearGroupsConfig[groupId];
        if (!yearGroupConfig) {
            return;
        }

        const copyYearConfig: QuotaCleanerYearGroupConfig = JSON.parse(JSON.stringify(yearGroupConfig));
        const { config } = copyYearConfig;
        Object.keys(config).forEach((year) => {
            const { totalCount, size } = config[year];
            if (totalCount <= 0 || size <= 0) {
                delete config[year];
            }
        });

        return copyYearConfig;
    }
);

export const getYearCurrentGroupConfig = createSelector(
    (state) => state,
    getCurrentGroup,
    (state, group) => (group ? getYearGroupConfig(state, group.groupId) : null)
);

export const isYearCurrentGroupFilter = createSelector(getYearCurrentGroupConfig, (yearConfig) => {
    if (!yearConfig) {
        return false;
    }

    const validYearsCount = Object.values(yearConfig.config).reduce(
        (acc, { totalCount, size }) => (totalCount > 0 && size > 0 ? acc + 1 : acc),
        0
    );
    // фильр валиден, когда есть из чего выбирать
    return validYearsCount > 1;
});

export const getIsToggledYearFilterOpen = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isToggledYearFilterOpen);
export const getIsItemsDeleteProcess = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isItemsDeleteProcess);
export const getIsItemsDeleteError = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.isItemsDeleteError);

const getFastCleanState = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.fastCleanState);

const getExpectedClearedNonMemo = (state: QuotaFastCleanerState) => state.fastCleanGroups.reduce((acc, group) => acc + group.size_uniq, 0);

const getFastCleanMessages = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => {
    const groups = state.fastCleanState.fastCleanGroups;

    const messagesRemove = groups.filter(
        (group) =>
            group.groupType === UserQuotaGroupType.Letter &&
            (group.groupId === UserQuotaGroupId.MailSpam || group.groupId === UserQuotaGroupId.MailBin)
    );

    const messagesSafe = groups.filter(
        (group) =>
            group.groupType === UserQuotaGroupType.Letter &&
            group.groupId !== UserQuotaGroupId.MailSpam &&
            group.groupId !== UserQuotaGroupId.MailBin
    );

    return {
        messagesRemove,
        messagesSafe,
        messagesRemoveSize: messagesRemove.reduce((prev, group) => prev + group.size_uniq, 0),
        messagesSafeSize: messagesSafe.reduce((prev, group) => prev + group.size_uniq, 0),
    };
});

const getFastCleanCloud = createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => {
    const groups = state.fastCleanState.fastCleanGroups;

    const cloudRemove = groups.filter(
        (group) => group.groupType === UserQuotaGroupType.Cloud && group.groupId === UserQuotaGroupId.CloudBin
    );

    const cloudSafe = groups.filter((group) => group.groupType === UserQuotaGroupType.Cloud && group.groupId !== UserQuotaGroupId.CloudBin);

    return {
        cloudRemove,
        cloudSafe,
        cloudRemoveSize: cloudRemove.reduce((prev, group) => prev + group.size_uniq, 0),
        cloudSafeSize: cloudSafe.reduce((prev, group) => prev + group.size_uniq, 0),
    };
});

const getFastCleanerGroups = createSelector(getFastCleanState, (state: QuotaFastCleanerState) => state.fastCleanGroups);
const getFastCleanerGroup = createSelector(
    getFastCleanerGroups,
    (state, groupId: UserQuotaGroupId) => groupId,
    (groups: QuotaCleaneFastGroup[], groupId: UserQuotaGroupId) => groups.find((group) => group.groupId === groupId)
);

export const QuotaCleanerSelectors = {
    getState: getQuotaCleanerState,
    getCurrentGroup,
    getCurrentGroupType,
    isCurrentGroupLetter,
    isCurrentGroupCloud,
    getEntryPoint: createSelector(getQuotaCleanerState, (state: QuotaCleanerState) => state.entryPoint),
};

export const FastCleanSelectors = {
    getState: getFastCleanState,
    getGroups: getFastCleanerGroups,
    getGroup: getFastCleanerGroup,
    getGroupParams: getFastGroupsParams,
    getIsLoaded: createSelector(getFastCleanState, (state: QuotaFastCleanerState) => state.isLoaded),
    getCleaningState: createSelector(getFastCleanState, (state: QuotaFastCleanerState) => state.cleaningState),
    getCanFastClean: createSelector(getFastCleanState, (state: QuotaFastCleanerState) => state.fastCleanGroups.length !== 0),
    getExpectedClearedNonMemo,
    getExpectedCleared: createSelector(getFastCleanState, getExpectedClearedNonMemo),
    getCleared: createSelector(getFastCleanState, (state: QuotaFastCleanerState) => state.cleared),
    getMessages: getFastCleanMessages,
    getCloud: getFastCleanCloud,
};
